import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { resolveBookServicePrice, toPosAmount } from '@/lib/bookingCatalog'

/** @typedef {'ladies' | 'gents'} BookPricingGender */

/** @typedef {{ serviceId: number, name: string, price: string, price_ladies: string, price_gents: string, qty: number }} CartLine */

const BookCartContext = createContext(null)

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function BookCartProvider({ children }) {
  const [lines, setLines] = useState(/** @type {CartLine[]} */ ([]))
  const [pricingGender, setPricingGenderState] = useState(/** @type {BookPricingGender} */ ('ladies'))

  const addService = useCallback((service) => {
    setLines((current) => {
      const priceLadies = toPosAmount(service.price_ladies ?? service.price)
      const priceGents = toPosAmount(service.price_gents)
      const unitPrice = resolveBookServicePrice(
        { price_ladies: priceLadies, price_gents: priceGents },
        pricingGender,
      )

      const existing = current.find((line) => line.serviceId === service.id)
      if (existing) {
        return current.map((line) =>
          line.serviceId === service.id ? { ...line, qty: line.qty + 1 } : line,
        )
      }

      return [
        ...current,
        {
          serviceId: service.id,
          name: service.name,
          price: unitPrice.toFixed(2),
          price_ladies: String(priceLadies),
          price_gents: String(priceGents),
          qty: 1,
        },
      ]
    })
  }, [pricingGender])

  const setPricingGender = useCallback((gender) => {
    setPricingGenderState(gender)
    setLines((current) =>
      current
        .map((line) => {
          const unitPrice = resolveBookServicePrice(
            { price_ladies: line.price_ladies, price_gents: line.price_gents },
            gender,
          )

          return {
            ...line,
            price: unitPrice.toFixed(2),
          }
        })
        .filter((line) => Number(line.price) > 0),
    )
  }, [])

  const updateQty = useCallback((serviceId, delta) => {
    setLines((current) =>
      current
        .map((line) =>
          line.serviceId === serviceId ? { ...line, qty: line.qty + delta } : line,
        )
        .filter((line) => line.qty > 0),
    )
  }, [])

  const clearCart = useCallback(() => setLines([]), [])

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => sum + Number(line.price) * line.qty, 0)

    return {
      subtotal: subtotal.toFixed(2),
      vat: (subtotal * 0.05).toFixed(2),
      total: (subtotal * 1.05).toFixed(2),
      count: lines.reduce((sum, line) => sum + line.qty, 0),
    }
  }, [lines])

  const value = useMemo(
    () => ({
      lines,
      totals,
      pricingGender,
      setPricingGender,
      addService,
      updateQty,
      clearCart,
      qtyFor: (serviceId) => lines.find((line) => line.serviceId === serviceId)?.qty ?? 0,
    }),
    [lines, totals, pricingGender, setPricingGender, addService, updateQty, clearCart],
  )

  return <BookCartContext.Provider value={value}>{children}</BookCartContext.Provider>
}

export function useBookCart() {
  const context = useContext(BookCartContext)
  if (!context) {
    throw new Error('useBookCart must be used within BookCartProvider')
  }

  return context
}
